import { useAppDispatch, useAppSelector } from '@unistocks/shared/hooks/redux-hooks';
import { AppActions, selectSidebarPinned } from '@unistocks/shared/store/root.slice';
import { PropsWithRef } from '@unistocks/types';
import { useState } from 'react';
import { Aside, GetProps, styled, useMedia } from 'tamagui';

export const SIDEBAR_COLLAPSED_WIDTH = 72;
export const SIDEBAR_EXPANDED_WIDTH = 280;

const SidebarImpl = styled(Aside, {
  name: 'Sidebar',
  position: 'fixed',
  left: 0,
  top: 0,
  h: '100vh',
  minW: SIDEBAR_COLLAPSED_WIDTH,
  overflow: 'hidden',
  zIndex: '$1',
  background: '$background',
  btrr: '$3',
  bbrr: '$3',

  variants: {
    expanded: {
      true: {
        w: SIDEBAR_EXPANDED_WIDTH
      },
      false: {
        w: SIDEBAR_COLLAPSED_WIDTH,

        hoverStyle: {
          w: SIDEBAR_EXPANDED_WIDTH
        }
      }
    }
  } as const,

  defaultVariants: {
    expanded: false
  }
});

export type SidebarState = {
  /**
   * Whether the sidebar is currently expanded (wide) or collapsed (narrow).
   */
  expanded: boolean;
  /**
   * Whether the sidebar is currently being hovered by the user.
   */
  hovered: boolean;
  /**
   * Whether the sidebar is pinned open by the user.
   */
  pinned: boolean;
};

export type SidebarProps = PropsWithRef<
  Omit<GetProps<typeof SidebarImpl>, 'children'> & {
    children: (state: SidebarState, togglePinned: (value?: boolean) => void) => React.ReactNode;
  },
  typeof SidebarImpl
>;

export const Sidebar = ({ ref, children, $lg, ...props }: SidebarProps) => {
  const dispatch = useAppDispatch();
  const pinned = useAppSelector(selectSidebarPinned);
  const media = useMedia();
  const [hovered, setHovered] = useState(false);

  const togglePinned = (value?: boolean) => {
    dispatch(AppActions.toggleSidebarPinned(value));
  };

  const expanded = hovered || (pinned && media.lg);

  return (
    <SidebarImpl
      ref={ref}
      $lg={{ expanded: pinned }}
      boxShadow={`8px 0 24px $shadow2`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      {...props}
    >
      {children({ expanded, hovered, pinned }, togglePinned)}
    </SidebarImpl>
  );
};
